import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Contrast, Type, Globe, X, Sparkles, Check, Sliders, Eye, BookOpen, VolumeX, Shield, Radio } from 'lucide-react';
import { switchScanner, SwitchScanMode } from '../utils/switchScanningEngine';
import { touchDwellEngine } from '../utils/touchDwellEngine';
import { ReadingRulerOverlay } from './ReadingRulerOverlay';

export const AccessibilityToolbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);
  const [readingRulerEnabled, setReadingRulerEnabled] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [switchScanMode, setSwitchScanMode] = useState<SwitchScanMode>('off');
  const [scanSpeed, setScanSpeed] = useState(1500);
  const [dwellDelay, setDwellDelay] = useState(0);

  const handleDwellDelayChange = (delay: number) => {
    setDwellDelay(delay);
    touchDwellEngine.setConfig({ enabled: delay > 0, delayMs: delay });
  };

  // High contrast mode
  const toggleHighContrast = () => {
    const next = !highContrast;
    setHighContrast(next);
    document.body.classList.toggle('high-contrast', next);
  };

  // Dyslexic font mode
  const toggleDyslexicFont = () => {
    const next = !dyslexicFont;
    setDyslexicFont(next);
    document.body.classList.toggle('font-opendyslexic', next);
  };

  // Reduced motion toggle
  const toggleReducedMotion = () => {
    const next = !reducedMotion;
    setReducedMotion(next);
    document.body.classList.toggle('prefers-reduced-motion', next);
  };

  // Switch scanner configuration
  const handleSwitchModeChange = (mode: SwitchScanMode) => {
    setSwitchScanMode(mode);
    switchScanner.setConfig({ mode, speedMs: scanSpeed, audioCue: true });
  };

  const handleScanSpeedChange = (speed: number) => {
    setScanSpeed(speed);
    switchScanner.setConfig({ speedMs: speed });
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <>
      {/* Global Reading Ruler Overlay */}
      <ReadingRulerOverlay enabled={readingRulerEnabled} />

      <div className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40">
        
        {/* Floating Popover Drawer - Clarvos Universal Accessibility Dock */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 w-88 max-h-[85vh] overflow-y-auto bg-[#1c1b1f]/98 text-white p-6 rounded-[32px] border border-white/15 shadow-2xl backdrop-blur-2xl space-y-5 animate-fade-in custom-scrollbar">
            
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#ffd64e] text-black flex items-center justify-center font-black text-xs shadow-sm">
                  ☼
                </div>
                <div>
                  <span className="text-xs font-black uppercase tracking-wider text-white block">
                    Universal A11y & Switch Dock
                  </span>
                  <span className="text-[10px] text-white/60">WCAG 2.1 & RPwD Act Compliant</span>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition cursor-pointer"
                aria-label="Close sensory dock"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* 1. Hardware Switch Scanning Engine (For Quadriplegia, CP, Motor Limits) */}
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-black text-[#ffd64e]">
                  <Radio className="w-3.5 h-3.5" />
                  <span>Switch Access Engine</span>
                </div>
                <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  Space / Enter
                </span>
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { id: 'off', label: 'Off' },
                  { id: '1-switch-auto', label: '1-Switch Auto' },
                  { id: '2-switch-step', label: '2-Switch Step' }
                ].map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => handleSwitchModeChange(mode.id as any)}
                    className={`py-2 px-1.5 rounded-xl text-[10px] font-black transition-all border cursor-pointer ${
                      switchScanMode === mode.id
                        ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-sm'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              {switchScanMode === '1-switch-auto' && (
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-[10px] text-white/60 font-mono">
                    <span>Scan Speed:</span>
                    <span className="text-[#ffd64e] font-bold">{(scanSpeed / 1000).toFixed(1)}s</span>
                  </div>
                  <input
                    type="range"
                    min="800"
                    max="3000"
                    step="200"
                    value={scanSpeed}
                    onChange={(e) => handleScanSpeedChange(parseInt(e.target.value))}
                    className="w-full accent-[#ffd64e] h-1.5 bg-white/10 rounded-lg cursor-pointer"
                  />
                </div>
              )}
            </div>

            {/* 1.5. Motor Tremor & Touch Acceptance Delay (Cerebral Palsy / Tremors) */}
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-black text-[#ffd64e]">
                  <Sliders className="w-3.5 h-3.5" />
                  <span>Touch Dwell / Acceptance Delay</span>
                </div>
                <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  {dwellDelay === 0 ? 'Instant Tap' : `${dwellDelay}ms Hold`}
                </span>
              </div>
              <p className="text-[10px] text-white/60 leading-tight">
                Filters out accidental double-taps and tremors for spastic CP and motor ataxia.
              </p>
              <div className="space-y-1">
                <input
                  type="range"
                  min="0"
                  max="1200"
                  step="100"
                  value={dwellDelay}
                  onChange={(e) => handleDwellDelayChange(parseInt(e.target.value))}
                  className="w-full accent-[#ffd64e] h-1.5 bg-white/10 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[9px] text-white/40 font-mono">
                  <span>0ms (Standard)</span>
                  <span>600ms (Moderate)</span>
                  <span>1200ms (High Tremor)</span>
                </div>
              </div>
            </div>

            {/* 2. Dyslexia Reading Tools */}
            <div className="space-y-2">
              <div className="text-[10px] font-black text-white/50 uppercase tracking-wider">
                Dyslexia & Reading Focus
              </div>

              {/* Reading Ruler Toggle */}
              <button
                onClick={() => setReadingRulerEnabled(!readingRulerEnabled)}
                className={`w-full p-3 rounded-2xl border text-xs font-black transition-all flex items-center justify-between cursor-pointer ${
                  readingRulerEnabled 
                    ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md' 
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <BookOpen className="w-4 h-4" />
                  <span>Reading Ruler Focus Band</span>
                </div>
                {readingRulerEnabled && <Check className="w-4 h-4 text-black" />}
              </button>

              {/* OpenDyslexic Font */}
              <button
                onClick={toggleDyslexicFont}
                className={`w-full p-3 rounded-2xl border text-xs font-black transition-all flex items-center justify-between cursor-pointer ${
                  dyslexicFont 
                    ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md' 
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Type className="w-4 h-4" />
                  <span>OpenDyslexic Typeface</span>
                </div>
                {dyslexicFont && <Check className="w-4 h-4 text-black" />}
              </button>
            </div>

            {/* 3. Sensory Overload & Vision Accommodations */}
            <div className="space-y-2">
              <div className="text-[10px] font-black text-white/50 uppercase tracking-wider">
                Sensory & Visual Ergonomics
              </div>

              {/* High Contrast Luminance */}
              <button
                onClick={toggleHighContrast}
                className={`w-full p-3 rounded-2xl border text-xs font-black transition-all flex items-center justify-between cursor-pointer ${
                  highContrast 
                    ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md' 
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Contrast className="w-4 h-4" />
                  <span>High Contrast Mode (#ffff00)</span>
                </div>
                {highContrast && <Check className="w-4 h-4 text-black" />}
              </button>

              {/* Reduced Motion Shield */}
              <button
                onClick={toggleReducedMotion}
                className={`w-full p-3 rounded-2xl border text-xs font-black transition-all flex items-center justify-between cursor-pointer ${
                  reducedMotion 
                    ? 'bg-[#ffd64e] text-black border-[#ffd64e] shadow-md' 
                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <Shield className="w-4 h-4" />
                  <span>Sensory Shield (Reduce Motion)</span>
                </div>
                {reducedMotion && <Check className="w-4 h-4 text-black" />}
              </button>
            </div>

            {/* 4. Multilingual Clinical Voice */}
            <div className="space-y-2 pt-2 border-t border-white/10">
              <div className="text-[10px] font-black text-white/50 uppercase tracking-wider">
                Clinical Speech Synthesis Language
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { code: 'en', label: 'English' },
                  { code: 'hi', label: 'हिंदी' },
                  { code: 'mr', label: 'मराठी' }
                ].map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`py-2 rounded-xl text-xs font-black transition-all border cursor-pointer ${
                      i18n.language === lang.code ? 'bg-[#ffd64e] text-black border-[#ffd64e]' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/15'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* Floating Action Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle Universal Sensory and Switch Accessibility Dock"
          className="px-4 py-2.5 rounded-full bg-[#1c1b1f] hover:bg-[#222222] text-[#ffd64e] border border-white/20 shadow-2xl flex items-center gap-2 font-black text-xs uppercase tracking-wider transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer"
        >
          <Sparkles className="w-4 h-4 text-[#ffd64e]" />
          <span className="hidden sm:inline text-white">Sensory & Switch A11y</span>
        </button>

      </div>
    </>
  );
};
